import {
  ClipboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
  type SubmitEvent
} from 'react'
import { useBeforeUnload, useBlocker } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  CheckIcon,
  ModalDialog,
  Mtds,
  Team,
  toSaveErrorMessage,
  XIcon
} from 'prove-common'
import { useTeamMutation } from './useTeamMutation'
import {
  emptyForm,
  findDuplicateTeamNames,
  findExistingTeamDuplicates,
  getCreateSuccessMessage,
  parseTeamNames,
  type TeamCreateFailure,
  type TeamFormState,
  type UnitOption
} from './teamsUtils'

export type TeamModalProps = {
  isOpen: boolean
  mode: 'create' | 'edit'
  initialTeam?: Team
  existingTeams: Team[]
  initialOwner?: { avdelingId?: string; seksjonId?: string }
  avdelingOptions: UnitOption[]
  seksjonOptions: UnitOption[]
  onClose: () => void
  organizationUnavailable: boolean
}

export const TeamModal = ({
  isOpen,
  mode,
  initialTeam,
  existingTeams,
  initialOwner,
  avdelingOptions,
  seksjonOptions,
  onClose,
  organizationUnavailable
}: TeamModalProps) => {
  const [returnFocusTarget] = useState(() => document.activeElement)
  useEffect(
    () => () => {
      if (
        returnFocusTarget instanceof HTMLElement &&
        returnFocusTarget.isConnected
      )
        returnFocusTarget.focus()
    },
    [returnFocusTarget]
  )
  const queryClient = useQueryClient()
  const initialCreateAvdelingId = initialOwner?.avdelingId ?? ''
  const initialCreateSeksjonId = initialOwner?.seksjonId ?? ''
  const initialAvdelingId =
    seksjonOptions.find(({ value }) => value === initialTeam?.seksjonId)
      ?.avdelingId ??
    initialTeam?.avdelingId ??
    initialCreateAvdelingId
  const [form, setForm] = useState<TeamFormState>(() =>
    initialTeam
      ? {
          name: initialTeam.name,
          avdelingId: initialTeam.avdelingId ?? '',
          seksjonId: initialTeam.seksjonId ?? ''
        }
      : {
          ...emptyForm,
          avdelingId: initialCreateAvdelingId,
          seksjonId: initialCreateSeksjonId
        }
  )
  const [selectedAvdelingId, setSelectedAvdelingId] = useState(
    initialAvdelingId ?? ''
  )
  const [teamNamesText, setTeamNamesText] = useState(
    initialTeam?.name ?? emptyForm.name
  )
  const [showValidation, setShowValidation] = useState(false)
  const createTeam = useTeamMutation('create', { invalidateOnSuccess: false })
  const updateTeam = useTeamMutation('edit')
  const [isSubmitting, setSubmitting] = useState(false)
  const submittingRef = useRef(false)
  const [saveError, setSaveError] = useState('')
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const summaryRef = useRef<HTMLHeadingElement>(null)
  const errorRef = useRef<HTMLHeadingElement>(null)
  const isPending = isSubmitting || createTeam.isPending || updateTeam.isPending
  const isDirty =
    teamNamesText !== (initialTeam?.name ?? '') ||
    (form.avdelingId ?? '') !==
      (initialTeam?.avdelingId ?? initialCreateAvdelingId) ||
    (form.seksjonId ?? '') !==
      (initialTeam?.seksjonId ?? initialCreateSeksjonId)
  const blocker = useBlocker(
    () => isDirty || isPending || submittingRef.current
  )
  useBeforeUnload((event) => {
    if (isDirty || isPending || submittingRef.current) {
      event.preventDefault()
      event.returnValue = ''
    }
  })
  const requestClose = () => {
    if (submittingRef.current) return
    if (isDirty) setConfirmDiscard(true)
    else onClose()
  }
  const modalId = mode === 'create' ? 'create-team-modal' : 'edit-team-modal'

  const selectedAvdeling = avdelingOptions.filter(
    ({ value }) => value === selectedAvdelingId
  )
  const filteredSeksjonOptions = selectedAvdelingId
    ? seksjonOptions.filter(
        ({ avdelingId }) => avdelingId === selectedAvdelingId
      )
    : seksjonOptions
  const avdelingSuggestionOptions = avdelingOptions.map(({ label, value }) => ({
    label,
    value
  }))
  const seksjonSuggestionOptions = filteredSeksjonOptions.map(
    ({ label, value }) => ({ label, value })
  )
  const selectedSeksjon = seksjonOptions.filter(
    ({ value }) => value === form.seksjonId
  )
  const parsedTeamNames = useMemo(
    () => parseTeamNames(teamNamesText),
    [teamNamesText]
  )
  const duplicateTeamNames = useMemo(
    () => findDuplicateTeamNames(parsedTeamNames),
    [parsedTeamNames]
  )
  const hasOwner = !!form.seksjonId || !!form.avdelingId
  const duplicateExistingNames = useMemo(() => {
    if (mode !== 'create') return []

    return findExistingTeamDuplicates(parsedTeamNames, existingTeams, {
      avdelingId: form.avdelingId,
      seksjonId: form.seksjonId
    })
  }, [existingTeams, form.avdelingId, form.seksjonId, mode, parsedTeamNames])

  const isCreateMode = mode === 'create'
  const hasDuplicateNameConflicts =
    isCreateMode &&
    (duplicateTeamNames.length > 0 || duplicateExistingNames.length > 0)
  const isFormValid =
    hasOwner && parsedTeamNames.length > 0 && !hasDuplicateNameConflicts
  const ownerError =
    showValidation && !hasOwner ? 'Velg avdeling eller seksjon' : undefined
  const nameError =
    [
      showValidation && !parsedTeamNames.length
        ? 'Legg inn minst ett teamnavn'
        : '',
      isCreateMode && duplicateTeamNames.length
        ? `Dupliserte teamnavn i listen: ${duplicateTeamNames.join(', ')}`
        : '',
      duplicateExistingNames.length
        ? `Team finnes allerede for valgt avdeling/seksjon: ${duplicateExistingNames.join(', ')}`
        : ''
    ]
      .filter(Boolean)
      .join('. ') || undefined

  const submitEditTeam = async (team: Team) => {
    await updateTeam.mutateAsync({
      id: team.id,
      ...form,
      name: parsedTeamNames[0]
    })
    Mtds.toast.success('Teamet er oppdatert')
    onClose()
  }

  const createTeamsForOwner = async () => {
    const failures: TeamCreateFailure[] = []
    let successCount = 0

    for (const name of parsedTeamNames) {
      try {
        await createTeam.mutateAsync({ ...form, name })
        successCount += 1
      } catch (error) {
        failures.push({
          name,
          message: toSaveErrorMessage(error, 'opprette teamet')
        })
      }
    }

    if (successCount > 0) {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['teams'] }),
        queryClient.invalidateQueries({ queryKey: ['team'] })
      ])
    }

    return { failures, successCount }
  }

  const handleCreateResult = (result: {
    failures: TeamCreateFailure[]
    successCount: number
  }) => {
    const { failures, successCount } = result

    if (successCount > 0) {
      Mtds.toast.success(getCreateSuccessMessage(successCount))
    }

    if (failures.length > 0) {
      setTeamNamesText(failures.map(({ name }) => name).join('\n'))
      setSaveError(
        `${successCount} team er bekreftet lagret. Lagring er ikke bekreftet for: ${failures.map(({ name, message }) => `${name}: ${message}`).join('; ')}. Bare navn uten bekreftet lagring står igjen. Kontroller teamlisten før du prøver igjen; ved nettverksfeil kan team allerede være lagret.`
      )
      setTimeout(() => errorRef.current?.focus())
      return
    }

    onClose()
  }

  const handleSubmit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (submittingRef.current || organizationUnavailable) return
    setShowValidation(true)
    if (!isFormValid) {
      setTimeout(() => summaryRef.current?.focus())
      return
    }
    submittingRef.current = true
    setSubmitting(true)
    setSaveError('')

    try {
      if (mode === 'edit' && initialTeam != null) {
        await submitEditTeam(initialTeam)
        return
      }

      const result = await createTeamsForOwner()
      handleCreateResult(result)
    } catch (error) {
      setSaveError(toSaveErrorMessage(error, 'lagre teamet'))
      setTimeout(() => errorRef.current?.focus())
    } finally {
      submittingRef.current = false
      setSubmitting(false)
    }
  }

  const handleTeamNamesPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const pastedText = event.clipboardData.getData('text')
    if (!pastedText) return

    const normalizedText = pastedText.replace(/\r\n?/g, '\n')
    if (!/[\n\t;]/.test(normalizedText)) return

    const pastedNames = normalizedText
      .split(/[\n\t;]/)
      .map((name) => name.trim())
      .filter((name) => name.length > 0)

    if (pastedNames.length === 0) return

    event.preventDefault()
    const target = event.currentTarget
    const selectionStart = target.selectionStart ?? target.value.length
    const selectionEnd = target.selectionEnd ?? target.value.length
    const before = target.value.slice(0, selectionStart)
    const after = target.value.slice(selectionEnd)
    const separatorBefore =
      before.length > 0 && !before.endsWith('\n') ? '\n' : ''
    const separatorAfter =
      after.length > 0 && !after.startsWith('\n') ? '\n' : ''

    setTeamNamesText(
      `${before}${separatorBefore}${pastedNames.join('\n')}${separatorAfter}${after}`
    )
  }

  return (
    <ModalDialog
      id={modalId}
      closedby={isPending ? 'none' : 'any'}
      open={isOpen}
      onClose={requestClose}
      onCancel={(event) => {
        event.preventDefault()
        requestClose()
      }}
      aria-labelledby={`${modalId}-title`}
    >
      <Mtds.Button
        type='button'
        command='request-close'
        commandfor={modalId}
        disabled={isPending}
        onClick={requestClose}
        aria-label='Lukk'
      />
      <Mtds.Grid data-gap='5'>
        <Mtds.Heading as='h2' id={`${modalId}-title`}>
          {mode === 'create' ? 'Legg til team' : 'Rediger team'}
        </Mtds.Heading>
        {(confirmDiscard || blocker.state === 'blocked') && (
          <Mtds.Alert data-color='warning'>
            <Mtds.Heading as='h3' data-size='sm'>
              Ulagrede endringer
            </Mtds.Heading>
            <p>
              {isPending
                ? 'Lagring pågår. Vent til den er ferdig.'
                : 'Vil du forkaste endringene som ikke er lagret?'}
            </p>
            <Mtds.Flex>
              <Mtds.Button
                type='button'
                data-variant='secondary'
                autoFocus
                onClick={() => {
                  setConfirmDiscard(false)
                  if (blocker.state === 'blocked') blocker.reset()
                }}
              >
                Fortsett redigering
              </Mtds.Button>
              <Mtds.Button
                type='button'
                disabled={isPending}
                data-color='danger'
                onClick={() => {
                  if (blocker.state === 'blocked') blocker.proceed()
                  else onClose()
                }}
              >
                Forkast endringer
              </Mtds.Button>
            </Mtds.Flex>
          </Mtds.Alert>
        )}
        {organizationUnavailable && (
          <Mtds.Alert data-color='warning'>
            Organisasjonsdata eller teamoversikten er utilgjengelig. Endringene
            er beholdt. Hent data på nytt før du lagrer.
            <Mtds.Button
              type='button'
              data-variant='secondary'
              onClick={() =>
                void queryClient.refetchQueries({
                  type: 'active',
                  predicate: (query) =>
                    ['teams', 'regioner', 'avdelinger', 'seksjoner'].includes(
                      String(query.queryKey[0])
                    )
                })
              }
            >
              Hent data på nytt
            </Mtds.Button>
          </Mtds.Alert>
        )}
        {saveError && (
          <Mtds.Alert data-color='danger' role='alert'>
            <Mtds.Heading as='h3' data-size='sm' ref={errorRef} tabIndex={-1}>
              Lagring mislyktes
            </Mtds.Heading>
            <p>{saveError}</p>
          </Mtds.Alert>
        )}
        <Mtds.Grid as='form' data-gap='5' noValidate onSubmit={handleSubmit}>
          <Mtds.Errorsummary hidden={!showValidation || isFormValid}>
            <Mtds.Heading as='h3' data-size='sm' ref={summaryRef} tabIndex={-1}>
              Rett opp feil før du lagrer
            </Mtds.Heading>
            <ul>
              {[
                [ownerError, 'team-avdeling'],
                [nameError, 'team-names']
              ].map(
                ([message, id]) =>
                  message && (
                    <li key={id}>
                      <a
                        href={`#${id}`}
                        onClick={(event) => {
                          event.preventDefault()
                          const target = document.getElementById(id!)
                          const input = target?.matches('input, textarea')
                            ? target
                            : target?.querySelector<HTMLElement>('input')
                          input?.focus()
                          input?.scrollIntoView?.({ block: 'nearest' })
                        }}
                      >
                        {message}
                      </a>
                    </li>
                  )
              )}
            </ul>
          </Mtds.Errorsummary>
          <Mtds.Grid data-gap='5'>
            <Mtds.Field
              as={Mtds.Field.Suggestion}
              id='team-avdeling'
              label='Avdeling'
              disabled={isPending}
              validation={ownerError}
              description='Velg avdeling eller seksjon. Velg begge for å knytte teamet til en seksjon i avdelingen.'
              placeholder='Velg avdeling (valgfritt)'
              selected={selectedAvdeling}
              options={avdelingSuggestionOptions}
              onSelectedChange={(items) => {
                const item = items?.[0]
                setSelectedAvdelingId(item?.value ?? '')
                setForm((current) => ({
                  ...current,
                  avdelingId: item?.value ?? '',
                  seksjonId: ''
                }))
              }}
            />
            <Mtds.Field
              as={Mtds.Field.Suggestion}
              id='team-seksjon'
              label='Seksjon'
              disabled={isPending}
              placeholder='Velg seksjon (valgfritt)'
              selected={selectedSeksjon}
              options={seksjonSuggestionOptions}
              onSelectedChange={(items) => {
                const item = items?.[0]
                setForm((current) => ({
                  ...current,
                  avdelingId: current.avdelingId,
                  seksjonId: item?.value ?? ''
                }))
              }}
            />
            {mode === 'create' ? (
              <>
                <Mtds.Field
                  as='textarea'
                  label='Teamnavn'
                  id='team-names'
                  required
                  disabled={isPending}
                  validation={nameError}
                  description='Ett teamnavn per linje for samme avdeling/seksjon. Du kan lime inn navn skilt med linjeskift, tabulator eller semikolon.'
                  name='teamNames'
                  rows={5}
                  value={teamNamesText}
                  onPaste={handleTeamNamesPaste}
                  onChange={(event) => {
                    setTeamNamesText(event.currentTarget.value)
                  }}
                />
                <Mtds.Muted>
                  {parsedTeamNames.length} team i listen. Duplikater må fjernes
                  før lagring.
                </Mtds.Muted>
              </>
            ) : (
              <Mtds.Field
                as='input'
                label='Navn'
                id='team-names'
                disabled={isPending}
                validation={nameError}
                name='name'
                value={teamNamesText}
                required
                onChange={(event) => {
                  setTeamNamesText(event.currentTarget.value)
                }}
              />
            )}
          </Mtds.Grid>
          <Mtds.Flex data-justify='end' data-gap='3'>
            <Mtds.Button
              type='button'
              disabled={isPending}
              onClick={requestClose}
            >
              <XIcon />
              Avbryt
            </Mtds.Button>
            <Mtds.Button
              data-variant='primary'
              type='submit'
              disabled={isPending || organizationUnavailable}
              aria-busy={isPending}
            >
              <CheckIcon />
              {isPending ? 'Lagrer…' : 'Lagre'}
            </Mtds.Button>
          </Mtds.Flex>
        </Mtds.Grid>
      </Mtds.Grid>
    </ModalDialog>
  )
}
